import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';
import { WebhooksEditForm } from './webhooks-edit.form';
import { Topics } from '@parashift/shared/constants';
import { PreventEnterDirective } from '@parashift/shared/directives';
import { Webhook, Topic, ListFilter } from '@parashift/shared/models';
import { ErrorResponse, WebhookService, TopicService, NotificationService, Logger, JsonApiQueryData } from '@parashift/shared/services';
import { ModalService } from '@parashift/shared/components';
import { HeaderActionStore } from '@parashift/shared/stores';

@Component({
  selector: 'webhooks-edit',
  templateUrl: './webhooks-edit.component.html',
  imports: [ReactiveFormsModule, PreventEnterDirective, FormlyModule]
})
export class WebhooksEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private webhookService = inject(WebhookService);
  private topicService = inject(TopicService);
  private modalService = inject(ModalService);
  private location = inject(Location);
  private notificationService = inject(NotificationService);
  private logger = inject(Logger);
  private headerActionStore = inject(HeaderActionStore);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  id: string;
  webhook: Webhook;
  topics: Topic[];
  error: ErrorResponse;
  webhooksEditForm: WebhooksEditForm;

  private tenant_id;

  constructor() {
    const destroyRef = this.destroyRef;
    destroyRef.onDestroy(() => this.headerActionStore.destroy());
  }

  ngOnInit() {
    this.headerActionStore.addActions({
      saveButton: { dispatch: () => this.webhooksEditForm?.model && this.saveWebhook(this.webhooksEditForm.model) },
      backButton: { dispatch: () => this.goToListView() },
      menu: {
        items: []
      }
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
      this.tenant_id = params.get('tenant_id');
      this.id = params.get('id');

      if (this.id !== 'new') {
        this.getWebhook(params.get('id'));
      } else {
        this.initWebhook();
      }
    });
  }

  getWebhook(id) {
    const listFilter = new ListFilter({
      include: 'topics'
    });

    this.webhookService.findRecord(id, listFilter, this.tenant_id).subscribe({
      next: (webhook: Webhook) => {
        this.id = webhook.id;
        this.getTopics(webhook);
        this.addDeleteButton();
      },
      error: (error: ErrorResponse) => {
        this.id = null;
        this.error = error;
        this.logger.log('webhooks-edit-component', 'WEBHOOK Error', error);
      }
    });
  }

  getTopics(webhook) {
    const slugs =  Topics.getSlugs();
    const filter = slugs ? { slug: slugs } : undefined;
    const listFilter = new ListFilter({
      filter,
      page: { size: 100 }
    });

    this.topicService.findAll(listFilter).subscribe({
      next: (topics: JsonApiQueryData<Topic>) => {
        this.topics = topics.getModels();
        this.setWebhookAndForm(webhook);
      },
      error: (error: ErrorResponse) => {
        this.error = error;
        this.logger.log('webhooks-edit-component', 'TOPIC Error', error);
      }
    });
  }

  setWebhookAndForm(webhook: Webhook) {
    this.webhook = webhook;
    this.id = webhook.id || 'new';
    const model = this.webhook.plainModel;
    model.topics = [];
    this.webhooksEditForm = new WebhooksEditForm(model, {}, this.getTopicsFormGroup());
  }

  initWebhook() {
    const webhook = new Webhook({ tenant_id: this.tenant_id });
    this.getTopics(webhook);
  }

  saveWebhook(model) {
    if (this.webhooksEditForm.form.valid) {
      model.topics = this.getSelectedTopics(model.topics);
      this.webhook.plainModel = model;

      this.webhookService.saveRecord(this.webhook, this.tenant_id).subscribe({
        next: (webhook: Webhook) => {
          this.notificationService.success({
            description: $localize `:@@components.webhooks.webhook_save_success:The webhook has been successfully saved.`,
            title: $localize `:@@components.webhooks.webhook_saved:Webhook saved`
          });

          if (this.id === 'new') {
            this.id = this.webhook.id = webhook.id;
            this.createAndReplaceUrl();
            this.addDeleteButton();
          }
        },
        error: (error: ErrorResponse) => {
          this.logger.log('Save Webhook Error', error);
          this.notificationService.formattedErrorResponse(error);
        }
      });
    }
  }

  deleteWebhook() {
    if (!this.id || this.id === 'new') {
      return;
    }

    this.modalService.confirmation({
      initialState: {
        title: $localize `:@@components.webhooks.delete_webhook:Delete webhook`,
        description: $localize `:@@components.webhooks.delete_webhook_confirm:Do you want to delete this webhook?`
      }
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      this.webhookService.deleteRecord(this.id, this.tenant_id).subscribe({
        next: () => {
          this.notificationService.success({
            description: $localize `:@@components.webhooks.webhook_delete_success:The webhook has been successfully deleted.`,
            title: $localize `:@@components.webhooks.webhook_deleted:Webhook deleted`
          });
          this.goToListView();
        },
        error: (error: ErrorResponse) => this.notificationService.formattedErrorResponse(error)
      });
    });
  }

  createAndReplaceUrl() {
    const url = this.router.createUrlTree(['../../', this.id, 'edit'], { relativeTo: this.route }).toString();
    this.location.replaceState(url);
    this.headerActionStore.replaceId(this.id);
  }

  goToListView() {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }

  private getTopicsFormGroup() {
    return this.topics.map(topic => {
      return {
        id: topic.id,
        name: topic.name,
        slug: topic.slug,
        checked: this.webhook.topics.some(associated_topic => {
          return associated_topic.id === topic.id;
        })
      };
    });
  }

  private getSelectedTopics(topics) {
    return this.topics.filter(topic => {
      return topics[topic.id];
    }).map(topic => {
      return topic.plainModel;
    });
  }

  private addDeleteButton() {
    this.headerActionStore.addMenuItems([{
      dispatch: () => this.webhook && this.deleteWebhook(),
      identifier: 'delete'
    }]);
  }
}
